/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  assertAccountExists,
  assertAccountsExist,
  combineCodec,
  decodeAccount,
  fetchEncodedAccount,
  fetchEncodedAccounts,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  getU128Decoder,
  getU128Encoder,
  getU16Decoder,
  getU16Encoder,
  getU32Decoder,
  getU32Encoder,
  getU64Decoder,
  getU64Encoder,
  getU8Decoder,
  getU8Encoder,
  transformEncoder,
  type Account,
  type Address,
  type EncodedAccount,
  type FetchAccountConfig,
  type FetchAccountsConfig,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type MaybeAccount,
  type MaybeEncodedAccount,
  type ReadonlyUint8Array,
} from '@solana/kit';

export const POOL_DISCRIMINATOR = new Uint8Array([
  241, 154, 109, 4, 17, 177, 109, 188,
]);

export function getPoolDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(POOL_DISCRIMINATOR);
}

export type Pool = {
  discriminator: ReadonlyUint8Array;
  config: Address;
  token0Mint: Address;
  token1Mint: Address;
  vault0: Address;
  vault1: Address;
  authority: Address;
  bump: number;
  reserve0: bigint;
  reserve1: bigint;
  totalShares: bigint;
  swapFeeBps: number;
  feeSplitBps: number;
  feeGrowthGlobal0Q64: bigint;
  feeGrowthGlobal1Q64: bigint;
  feesUnclaimed0: bigint;
  feesUnclaimed1: bigint;
  hookProgram: Address;
  hookFlags: number;
  liquidityMeasureTokenIndex: number;
  kLast: bigint;
  protocolFeePosition: Address;
  locked: number;
  version: number;
  reserved: ReadonlyUint8Array;
};

export type PoolArgs = {
  config: Address;
  token0Mint: Address;
  token1Mint: Address;
  vault0: Address;
  vault1: Address;
  authority: Address;
  bump: number;
  reserve0: number | bigint;
  reserve1: number | bigint;
  totalShares: number | bigint;
  swapFeeBps: number;
  feeSplitBps: number;
  feeGrowthGlobal0Q64: number | bigint;
  feeGrowthGlobal1Q64: number | bigint;
  feesUnclaimed0: number | bigint;
  feesUnclaimed1: number | bigint;
  hookProgram: Address;
  hookFlags: number;
  liquidityMeasureTokenIndex: number;
  kLast: number | bigint;
  protocolFeePosition: Address;
  locked: number;
  version: number;
  reserved: ReadonlyUint8Array;
};

/** Gets the encoder for {@link PoolArgs} account data. */
export function getPoolEncoder(): FixedSizeEncoder<PoolArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['config', getAddressEncoder()],
      ['token0Mint', getAddressEncoder()],
      ['token1Mint', getAddressEncoder()],
      ['vault0', getAddressEncoder()],
      ['vault1', getAddressEncoder()],
      ['authority', getAddressEncoder()],
      ['bump', getU8Encoder()],
      ['reserve0', getU64Encoder()],
      ['reserve1', getU64Encoder()],
      ['totalShares', getU128Encoder()],
      ['swapFeeBps', getU16Encoder()],
      ['feeSplitBps', getU16Encoder()],
      ['feeGrowthGlobal0Q64', getU128Encoder()],
      ['feeGrowthGlobal1Q64', getU128Encoder()],
      ['feesUnclaimed0', getU64Encoder()],
      ['feesUnclaimed1', getU64Encoder()],
      ['hookProgram', getAddressEncoder()],
      ['hookFlags', getU32Encoder()],
      ['liquidityMeasureTokenIndex', getU8Encoder()],
      ['kLast', getU128Encoder()],
      ['protocolFeePosition', getAddressEncoder()],
      ['locked', getU8Encoder()],
      ['version', getU8Encoder()],
      ['reserved', fixEncoderSize(getBytesEncoder(), 7)],
    ]),
    (value) => ({ ...value, discriminator: POOL_DISCRIMINATOR }),
  );
}

/** Gets the decoder for {@link Pool} account data. */
export function getPoolDecoder(): FixedSizeDecoder<Pool> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['config', getAddressDecoder()],
    ['token0Mint', getAddressDecoder()],
    ['token1Mint', getAddressDecoder()],
    ['vault0', getAddressDecoder()],
    ['vault1', getAddressDecoder()],
    ['authority', getAddressDecoder()],
    ['bump', getU8Decoder()],
    ['reserve0', getU64Decoder()],
    ['reserve1', getU64Decoder()],
    ['totalShares', getU128Decoder()],
    ['swapFeeBps', getU16Decoder()],
    ['feeSplitBps', getU16Decoder()],
    ['feeGrowthGlobal0Q64', getU128Decoder()],
    ['feeGrowthGlobal1Q64', getU128Decoder()],
    ['feesUnclaimed0', getU64Decoder()],
    ['feesUnclaimed1', getU64Decoder()],
    ['hookProgram', getAddressDecoder()],
    ['hookFlags', getU32Decoder()],
    ['liquidityMeasureTokenIndex', getU8Decoder()],
    ['kLast', getU128Decoder()],
    ['protocolFeePosition', getAddressDecoder()],
    ['locked', getU8Decoder()],
    ['version', getU8Decoder()],
    ['reserved', fixDecoderSize(getBytesDecoder(), 7)],
  ]);
}

/** Gets the codec for {@link Pool} account data. */
export function getPoolCodec(): FixedSizeCodec<PoolArgs, Pool> {
  return combineCodec(getPoolEncoder(), getPoolDecoder());
}

export function decodePool<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress>,
): Account<Pool, TAddress>;
export function decodePool<TAddress extends string = string>(
  encodedAccount: MaybeEncodedAccount<TAddress>,
): MaybeAccount<Pool, TAddress>;
export function decodePool<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress> | MaybeEncodedAccount<TAddress>,
): Account<Pool, TAddress> | MaybeAccount<Pool, TAddress> {
  return decodeAccount(
    encodedAccount as MaybeEncodedAccount<TAddress>,
    getPoolDecoder(),
  );
}

export async function fetchPool<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<Account<Pool, TAddress>> {
  const maybeAccount = await fetchMaybePool(rpc, address, config);
  assertAccountExists(maybeAccount);
  return maybeAccount;
}

export async function fetchMaybePool<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<MaybeAccount<Pool, TAddress>> {
  const maybeAccount = await fetchEncodedAccount(rpc, address, config);
  return decodePool(maybeAccount);
}

export async function fetchAllPool(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<Account<Pool>[]> {
  const maybeAccounts = await fetchAllMaybePool(rpc, addresses, config);
  assertAccountsExist(maybeAccounts);
  return maybeAccounts;
}

export async function fetchAllMaybePool(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<MaybeAccount<Pool>[]> {
  const maybeAccounts = await fetchEncodedAccounts(rpc, addresses, config);
  return maybeAccounts.map((maybeAccount) => decodePool(maybeAccount));
}

export function getPoolSize(): number {
  return 379;
}
